import { useMergeRefs } from "@floating-ui/react";
import classNames from "classnames";
import isString from "lodash/isString";
import { useTheme } from "PFApp/theme_context_provider/theme_context_provider_context";
import { Icon } from "PFComponents/icon";
import { IconName } from "PFTheme/graphics/icons";
import { ReactElement, Ref, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";

import { NavItem as NavItemType } from "../../types";
import { NavDropdown } from "./nav_dropdown";
import css from "./nav_item.module.scss";

export type NavItemProps = NavItemType & {
  ref?: Ref<HTMLElement>;
  counter?: number;
  counterHref?: string;
  icon: IconName | ReactElement;
  openKey?: string | null;
  forceOpen?: boolean;
  isActiveTest?: (path: string) => RegExpMatchArray | boolean | null;
  hoverEffect?: boolean;
  openSubMenu: (id: NavItemType["id"]) => void;
  closeSubMenu: (id?: NavItemType["id"]) => void;
  onClick?: VoidFunction;
} & React.AriaAttributes;

export type CommonNavItemProps = Pick<NavItemProps, "openKey" | "openSubMenu" | "closeSubMenu">;

export const NavItem = ({
  ref,
  icon,
  items,
  name,
  counter,
  href,
  id,
  openKey,
  forceOpen,
  isActiveTest,
  hoverEffect = true,
  "aria-label": ariaLabel,
  closeSubMenu,
  openSubMenu,
  onClick
}: NavItemProps) => {
  const location = useLocation();
  const { themeVars } = useTheme();
  const [active, setActive] = useState(false);
  const linkRef = useRef<any>(null);
  const mergedLinkRef = useMergeRefs([linkRef, ref]);

  const isMiddleClick = (e) => e.button === 1 || e.button === 4;

  const handleLinkClick = (e) => {
    if (isMiddleClick(e)) {
      return; // just follow href, otherwise ignore
    }
    if (onClick) {
      e.preventDefault(); // avoid navigation with href (because function)
      onClick();
    }
  };

  const handleButtonClick = (e) => {
    if (isMiddleClick(e)) {
      return; // just follow href, otherwise ignore
    }
    if (items) {
      openKey === id ? closeSubMenu() : openSubMenu(id);
    } else {
      closeSubMenu();
    }
    onClick?.();
  };

  const handleSubMenuClose: NavItemProps["closeSubMenu"] = (...args) => {
    linkRef.current?.focus();
    closeSubMenu(...args);
  };

  useEffect(() => {
    const onHashChange = () => isActiveTest && setActive(!!isActiveTest(location.pathname));
    isActiveTest && window.addEventListener("hashchange", onHashChange);
    onHashChange();
    return () => isActiveTest && window.removeEventListener("hashchange", onHashChange);
  }, [location.pathname]);

  const isOpen = openKey === id || forceOpen;
  const showsDropdown = items && isOpen;
  const showCounter = counter ? counter > 0 : false;
  const itemContent = (
    <div className={css.itemContent}>
      <div className={css.icon}>
        {isString(icon) ? (
          <Icon
            name={icon as IconName}
            size="lg"
            color={isOpen ? "paletteNavbarBackground" : "paletteWhite0"}
          />
        ) : (
          icon
        )}
      </div>
      {showCounter && <div className={css.counter}>{counter}</div>}
    </div>
  );

  const displayId = `${id}_nav_link`;
  const hasPopup = items && items.length > 0;
  const listId = `${displayId}_menuList`;
  const accessibleLabel = showCounter ? `${ariaLabel || name} (${counter})` : ariaLabel || name;

  const visualItems = (
    <>
      <svg className={css.blobTail} viewBox="0 0 100 90" width="100" height="90">
        <path d="M0,0V90H50C50,68 60,60 80,60 100,60 100,30 80,30 60,30 50,22 50,0Z" />
      </svg>
      <span className={css.blob} />

      <span className={css.openBlob} />
      <svg className={css.openBlobTail} viewBox="0 0 50 90" width="50" height="90">
        {items && items.length === 1 ? (
          <path d="M50,70 C50,70,45,60,20,60 C0,60,0,30,20,30 C45,30,50,20,50,20 h1 v50 Z" />
        ) : (
          <path d="M50,90C50,70 45,60 20,60 0,60 0,30 20,30 45,30 50,20 50,0 h1 v90z" />
        )}
      </svg>
    </>
  );

  return (
    <li
      data-qa-id={`NavItem.${id}`}
      id={id !== undefined ? String(id) : undefined}
      className={classNames(css.item, {
        [css.isOpen]: isOpen,
        [css.isActive]: active
      })}
    >
      {!hasPopup && href ? (
        <Link
          ref={mergedLinkRef}
          to={href}
          className={classNames(css.link, { [css.withHoverEffect]: hoverEffect })}
          onClick={handleLinkClick}
          id={displayId}
          aria-label={accessibleLabel}
          aria-current={active && "page"}
          role="link"
        >
          {visualItems}
          {itemContent}
        </Link>
      ) : (
        <button
          ref={mergedLinkRef}
          className={classNames(css.link, { [css.withHoverEffect]: hoverEffect })}
          onClick={handleButtonClick}
          id={displayId}
          aria-expanded={!onClick ? showsDropdown : undefined}
          aria-haspopup={!onClick ? hasPopup : undefined}
          aria-label={accessibleLabel}
          aria-controls={!onClick ? listId : undefined}
          aria-current={active && "page"}
        >
          {visualItems}
          {itemContent}
        </button>
      )}

      <div className={css.linkName} style={{ color: `${themeVars["paletteWhite0"]}` }}>
        {name}
      </div>

      {hasPopup && (
        <NavDropdown
          id={listId}
          items={items}
          closeSubMenu={handleSubMenuClose}
          shown={showsDropdown}
          labelledBy={displayId}
        />
      )}
    </li>
  );
};
